import moment from 'moment';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Container } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import Checkbox from '../../../../components/checkbox';
import Dropdown from '../../../../components/dropdown';
import Loading from '../../../../components/loading';
import BucketView from '../../../../components/scoreMonitor/bucketView';
import ChartView from '../../../../components/scoreMonitor/chartView';
import { AWARD_TYPES } from '../../../../models/award';
import Session, { SESSION_TYPES } from '../../../../models/session';
import ThresholdCalcSubmissions, {
  REGISTRATION_TYPES,
} from '../../../../models/thresholdCalculatorSubmissions';
import Hero from '../../../../modules/hero';
import styles from './styles.module.scss';

const ScoreMonitor = () => {
  const [sessions, setSessions] = useState([]);
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [predecessorSessionSubmissions, setPredecessorSessionSubmissions] =
    useState([]);
  const [selectedView, setSelectedView] = useState('chart');
  const [loading, setLoading] = useState('init');
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [enableSimulation, setEnableSimulation] = useState(false);
  const [simulationHourIndex, setSimulationHourIndex] = useState(0);
  const { t } = useTranslation();
  const { state } = useLocation();
  const debounceTimerRef = useRef(null);
  const [debouncedSimulationDateTime, setDebouncedSimulationDateTime] = useState(null);

  const currentSession = sessions?.find(
    (s) => s.sessionId == selectedSessionId
  );

  const sessionOptions = useMemo(() => {
    return sessions.map((session) => ({
      value: session.sessionId,
      text: session.sessionName,
    }));
  }, [sessions]);

  const simulationHours = useMemo(() => {
    if (!currentSession?.startDate || !currentSession?.endDate) {
      return [];
    }

    const start = moment(currentSession.startDate).startOf('day');
    const end = moment(currentSession.endDate).endOf('day');
    const hours = [];

    let current = start.clone();
    while (current.isSameOrBefore(end, 'hour')) {
      hours.push(current.clone());
      current.add(1, 'hour');
    }

    return hours;
  }, [currentSession]);

  const selectedSimulationDateTime = useMemo(() => {
    if (!enableSimulation || !simulationHours.length || simulationHourIndex < 0 || simulationHourIndex >= simulationHours.length) {
      return null;
    }
    return simulationHours[simulationHourIndex];
  }, [enableSimulation, simulationHours, simulationHourIndex]);

  // Debounce the simulation datetime for API calls
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSimulationDateTime(selectedSimulationDateTime);
    }, 500); // Wait 500ms after user stops sliding

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [selectedSimulationDateTime]);

  useEffect(() => {
    async function fetchSessions() {
      const sessionData = await Session.fetchAll({ archived: false });

      setSessions(sessionData);

      if (state?.sessionId) {
        setSelectedSessionId(state.sessionId);
      }
    }

    fetchSessions();
  }, [state]);

  useEffect(() => {
    if (!currentSession) return;

    async function fetchSubmissions({ mode = 'init' }) {
      setLoading(mode);

      const evaluationsUntil = debouncedSimulationDateTime 
        ? debouncedSimulationDateTime.toISOString()
        : null;

      const promises = [
        ThresholdCalcSubmissions.fetchAllSubmissions(
          currentSession.sessionId,
          evaluationsUntil
        ),
      ];

      if (currentSession.predecessorSessionId) {
        promises.push(
          ThresholdCalcSubmissions.fetchAllSubmissions(
            currentSession.predecessorSessionId,
            evaluationsUntil
          )
        );
      }

      const [_submissions, _predecessorSessionSubmissions] =
        await Promise.all(promises);

      const mappedSubmissions = _submissions.map((submission) => {
        return {
          ...submission,
          isCooperation:
            submission?.registrationType === REGISTRATION_TYPES.COOPERATION,
          isWildcard:
            submission?.registrationType === REGISTRATION_TYPES.WILDCARD,
        };
      });

      setSubmissions(mappedSubmissions);

      setPredecessorSessionSubmissions(_predecessorSessionSubmissions);
      setLoading('idle');
      setLastRefreshed(new Date().toLocaleString());
    }

    fetchSubmissions({ mode: 'init' });
  }, [
    currentSession,
    debouncedSimulationDateTime,
    setLoading,
    setSubmissions,
    setPredecessorSessionSubmissions,
    setLastRefreshed,
  ]);

  const handleSessionChange = (val) => {
    setSubmissions([]);
    setPredecessorSessionSubmissions([]);
    setSelectedView('chart');
    setSelectedSessionId(val);
    setEnableSimulation(false);
    setSimulationHourIndex(0);
    setDebouncedSimulationDateTime(null);
  };

  const handleSimulationToggle = (event) => {
    const checked = event.target.checked;
    setEnableSimulation(checked);
    if (!checked) {
      // Reset to first hour when toggling off, which will trigger a refetch to get current data
      setSimulationHourIndex(0);
      setDebouncedSimulationDateTime(null);
    } else if (simulationHours.length > 0) {
      // Set to first hour when enabling simulation
      setSimulationHourIndex(0);
      // Immediately set debounced value when enabling
      setDebouncedSimulationDateTime(simulationHours[0]);
    }
  };

  const handleSimulationHourChange = (event) => {
    setSimulationHourIndex(parseInt(event.target.value, 10));
  };

  return (
    <div className={styles.sessionsOverview}>
      <Hero
        title={t('sessions.threshold.title')}
        rightSection={
          <Dropdown
            options={sessionOptions}
            value={selectedSessionId}
            onChange={handleSessionChange}
            label={t('jurors.overview.dropdownLabel')}
            className={'w-100'}
          />
        }
      />
      {!currentSession ? (
        <Container className={'py-5'}>
          <span>{t('scoreMonitor.selectSession')}</span>
        </Container>
      ) : currentSession?.awardType !== AWARD_TYPES.IF_DA ? (
        <div className={styles.notAvailableContainer}>
          <h2>{t('scoreMonitor.notAvailable')}</h2>
        </div>
      ) : (
        <Container className={'py-5'}>
          <h1 className={styles.headline}>{currentSession?.sessionName}</h1>
          <p className={styles.subHeadline}>
            {loading === 'refresh' || loading === 'init' ? (
              <Loading />
            ) : (
              <span>
                {t('scoreMonitor.lastRefreshed', {
                  date: lastRefreshed,
                })}
              </span>
            )}
          </p>
          {loading !== 'init' && (
            <>
              <div className={styles.simulationControls}>
                <Checkbox
                  name="enableSimulation"
                  label={t('scoreMonitor.enableSimulation', 'Enable Simulation Mode')}
                  value={enableSimulation}
                  onChange={handleSimulationToggle}
                />
                {enableSimulation && simulationHours.length > 0 && (
                  <div className={styles.sliderContainer}>
                    <div className={styles.sliderLabels}>
                      <span className={styles.sliderLabel}>
                        {moment(currentSession.startDate).format('DD.MM.YYYY HH:mm')}
                      </span>
                      <span className={styles.sliderLabel}>
                        {moment(currentSession.endDate).format('DD.MM.YYYY HH:mm')}
                      </span>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max={simulationHours.length - 1}
                      value={simulationHourIndex}
                      step="1"
                      onChange={handleSimulationHourChange}
                      className={styles.slider}
                    />
                    {selectedSimulationDateTime && (
                      <div className={styles.selectedDateTime}>
                        {t('scoreMonitor.selectedDateTime', 'Selected:')}{' '}
                        <strong>
                          {selectedSimulationDateTime.format('DD.MM.YYYY HH:mm')}
                        </strong>
                      </div>
                    )}
                  </div>
                )}
              </div>
              {currentSession?.sessionType === SESSION_TYPES.FINAL_JURY && (
                <div className={styles.viewSelector}>
                  <button
                    className={selectedView === 'chart' ? styles.active : undefined}
                    onClick={() => setSelectedView('chart')}
                  >
                    {t('scoreMonitor.chartView.title')}
                  </button>
                  <button
                    className={selectedView === 'bucket' ? styles.active : undefined}
                    onClick={() => setSelectedView('bucket')}
                  >
                    {t('scoreMonitor.bucketView.title')}
                  </button>
                </div>
              )}
              {selectedView === 'chart' ? (
                <ChartView
                  submissions={submissions}
                  session={currentSession}
                  predecessorSessionSubmissions={predecessorSessionSubmissions}
                />
              ) : selectedView === 'bucket' ? (
                <BucketView
                  submissions={submissions}
                  session={currentSession}
                  predecessorSessionSubmissions={predecessorSessionSubmissions}
                />
              ) : null}
            </>
          )}
        </Container>
      )}
    </div>
  );
};

export default ScoreMonitor;
